import { useState } from 'react';

interface CaseStudy {
  id: number;
  title: string;
  sector: string;
  category: string;
  image: string;
  problem: string;
  solution: string;
  diagnosis: string;
  results: string;
  brandValue: string;
}

const caseStudies: CaseStudy[] = [
  {
    id: 1,
    title: 'Azienda Manifatturiera Italiana',
    sector: 'Manifatturiero',
    category: 'Valutazione',
    image: 'https://public.readdy.ai/ai/img_res/b401372996067a0dc963203524b52dea.jpg',
    problem: 'In fase di riorganizzazione societaria, l\'azienda necessitava di una valutazione certificata del brand asset per l\'inserimento a bilancio. I metodi tradizionali non riuscivano a quantificare il reale valore intangibile costruito in quattro decenni di eccellenza manifatturiera.',
    solution: 'Valutazione scientifica del brand asset secondo metodologia BCI e standard ISO 10668, con quantificazione del valore economico per inserimento patrimoniale a bilancio e governance strategica nelle operazioni di riorganizzazione societaria.',
    diagnosis: 'BCI Score: 8.2/10 - Brand con eccellente riconoscimento nel settore B2B, forte reputazione qualitativa e solida base patrimoniale. Identificate opportunità di valorizzazione nella comunicazione digitale per massimizzare il valore dell\'asset.',
    results: 'Brand asset inserito a bilancio con valutazione certificata. Incremento del 35% nella valutazione complessiva aziendale. Il riconoscimento patrimoniale del brand ha ottimizzato la struttura societaria e rafforzato la posizione negoziale nelle operazioni di riorganizzazione.',
    brandValue: '€12.5M'
  },
  {
    id: 2,
    title: 'Catena Retail Regionale',
    sector: 'Retail',
    category: 'Valutazione',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20modern%20retail%20store%20interior%20with%20sophisticated%20product%20displays%20and%20contemporary%20design%20featuring%20clean%20lines%20and%20professional%20lighting%20in%20a%20premium%20shopping%20environment%20with%20minimalist%20aesthetic&width=800&height=600&seq=2&orientation=landscape',
    problem: 'In fase di vendita quote societarie, mancava una valutazione oggettiva e comparabile del brand. Gli acquirenti richiedevano una metodologia certificata per quantificare il valore dell\'asset intangibile nelle operazioni di licensing e franchising.',
    solution: 'Valutazione BCI completa con analisi comparativa di mercato, quantificazione del valore del brand per operazioni di licensing e franchising, e certificazione secondo standard internazionali per garantire comparabilità nelle trattative.',
    diagnosis: 'BCI Score: 7.4/10 - Brand con forte presenza locale e potenziale di espansione. Eccellente fedeltà clienti e riconoscibilità regionale. Valore dell\'asset quantificato e certificato per operazioni di cessione quote.',
    results: 'Vendita quote completata con successo grazie alla valutazione certificata del brand. Il valore quantificato ha facilitato le trattative, garantendo trasparenza e comparabilità. Struttura di licensing definita per future espansioni franchising.',
    brandValue: '€8.3M'
  },
  {
    id: 3,
    title: 'Startup Tecnologica',
    sector: 'Tech',
    category: 'Governance',
    image: 'https://readdy.ai/api/search-image?query=Modern%20technology%20startup%20office%20with%20innovative%20workspace%20design%20featuring%20collaborative%20areas%20and%20cutting-edge%20equipment%20in%20a%20bright%20creative%20environment%20with%20contemporary%20furniture%20and%20professional%20atmosphere&width=800&height=600&seq=3&orientation=landscape',
    problem: 'In fase di negoziazione per partnership strategiche e potenziali operazioni di M&A, la startup necessitava di valorizzare il brand e implementare una governance strutturata per massimizzare il valore nelle operazioni di licensing e franchising tecnologico.',
    solution: 'Valorizzazione completa del brand con implementazione di sistema di governance strutturato. Misurazione scientifica del valore per operazioni di partnership, licensing, franchising e M&A. Certificazione secondo standard ISO 10668 per credibilità nelle negoziazioni.',
    diagnosis: 'BCI Score: 7.5/10 - Brand emergente con eccellente potenziale di crescita. Forte innovazione tecnologica e posizionamento distintivo. Governance implementata per protezione e valorizzazione dell\'asset nelle operazioni strategiche.',
    results: 'Brand valorizzato e governance attivata con successo. Partnership strategiche concluse con valutazione certificata dell\'asset. Sistema di licensing implementato per espansione controllata. Valore del brand riconosciuto come asset chiave nelle trattative M&A.',
    brandValue: '€4.2M'
  },
  {
    id: 4,
    title: 'Gruppo Alberghiero',
    sector: 'Hospitality',
    category: 'Valutazione',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20boutique%20hotel%20exterior%20and%20interior%20showcasing%20elegant%20architecture%20with%20refined%20hospitality%20design%20featuring%20premium%20amenities%20and%20sophisticated%20ambiance%20in%20an%20upscale%20tourism%20destination%20with%20professional%20service%20excellence&width=800&height=600&seq=4&orientation=landscape',
    problem: 'Il gruppo pianificava espansione attraverso partnership, licensing e franchising, ma necessitava di una misurazione certificata e comparabile del valore del brand per definire condizioni eque nelle operazioni e potenziali M&A.',
    solution: 'Misurazione scientifica e comparabilità del valore del brand portfolio (3 marchi) secondo metodologia BCI. Valutazione certificata per operazioni di partnership, licensing, franchising e M&A. Analisi del potenziale di brand extension e definizione fee structure.',
    diagnosis: 'BCI Score medio: 7.2/10 - Brand principale forte (8.1), marchi secondari con potenziale strategico. Eccellente reputazione e valore comparabile certificato per operazioni di espansione e potenziali acquisizioni.',
    results: 'Valore del brand certificato e comparabile per tutte le operazioni strategiche. Lancio programma franchising con fee structure basata su valutazione scientifica. 5 partnership attivate in 12 mesi. Royalty fees ottimizzate generano €850K di ricavi aggiuntivi annui.',
    brandValue: '€6.8M'
  },
  {
    id: 5,
    title: 'Azienda Vinicola Storica',
    sector: 'Food & Beverage',
    category: 'Governance',
    image: 'https://readdy.ai/api/search-image?query=Historic%20Italian%20vineyard%20estate%20with%20traditional%20wine%20cellar%20and%20modern%20production%20facilities%20showcasing%20premium%20winemaking%20heritage%20in%20a%20picturesque%20countryside%20setting%20with%20elegant%20tasting%20room%20and%20professional%20wine%20production%20environment&width=800&height=600&seq=5&orientation=landscape',
    problem: 'Passaggio generazionale in corso con necessità di misurazione certificata e comparabilità del valore del brand per garantire equità nella successione, preservare l\'heritage aziendale e definire governance per la continuità del valore.',
    solution: 'Misurazione scientifica del valore del brand secondo metodologia BCI per comparabilità nelle operazioni di passaggio generazionale. Implementazione governance strutturata per preservazione heritage e continuità del valore. Certificazione per trasparenza successoria.',
    diagnosis: 'BCI Score: 8.5/10 - Brand storico con eccellente valore patrimoniale e forte legame territoriale. Heritage quantificato e certificato. Governance implementata per transizione generazionale e preservazione del valore nel tempo.',
    results: 'Passaggio generazionale completato con successo grazie a valutazione certificata e comparabile. Governance attivata garantisce continuità del valore. Mantenimento 95% clientela storica, acquisizione nuovo segmento (+2.500 clienti). Export +40%.',
    brandValue: '€15.7M'
  },
  {
    id: 6,
    title: 'Studio Professionale Multidisciplinare',
    sector: 'Servizi Professionali',
    category: 'Valutazione',
    image: 'https://readdy.ai/api/search-image?query=Professional%20modern%20law%20office%20interior%20with%20sophisticated%20meeting%20rooms%20and%20contemporary%20workspace%20design%20featuring%20elegant%20furniture%20and%20premium%20business%20environment%20with%20refined%20corporate%20atmosphere%20and%20executive%20setting&width=800&height=600&seq=6&orientation=landscape',
    problem: 'Operazione di M&A tra due studi professionali con necessità di misurazione certificata e comparabilità del valore dei rispettivi brand per definire quote societarie, governance della nuova entità e condizioni eque di fusione.',
    solution: 'Doppia valutazione BCI certificata per misurazione e comparabilità scientifica del valore dei brand. Analisi comparativa dettagliata e valutazione del potenziale sinergico. Certificazione secondo standard internazionali per trasparenza nell\'operazione M&A.',
    diagnosis: 'Studio A - BCI: 7.8/10 | Studio B - BCI: 7.3/10. Valori certificati e comparabili. Forte reputazione locale per entrambi, complementarità nelle specializzazioni. Potenziale sinergico quantificato: +25% sul valore combinato.',
    results: 'M&A completata con successo grazie a valutazione certificata e comparabile. Quote definite scientificamente con piena trasparenza. Nuovo brand unificato raggiunge BCI 8.1 in 6 mesi. Fatturato consolidato +32% vs somma dei singoli.',
    brandValue: '€3.9M'
  },
  {
    id: 7,
    title: 'Impresa Settore Industriale',
    sector: 'Industriale',
    category: 'Governance',
    image: 'https://readdy.ai/api/search-image?query=Corporate%20business%20assets%20and%20intellectual%20property%20portfolio%20visualization%20with%20financial%20charts%20showing%20brand%20equity%20growth%20and%20company%20valuation%20metrics%2C%20professional%20wealth%20management%20dashboard%20displaying%20intangible%20assets%20value%2C%20sophisticated%20business%20patrimony%20analysis%20with%20modern%20digital%20interface%2C%20premium%20corporate%20treasury%20and%20brand%20capital%20representation%20with%20clean%20elegant%20design%2C%20strategic%20asset%20management%20environment%20with%20professional%20blue%20and%20gold%20tones&width=800&height=600&seq=governance-patrimonio-002&orientation=landscape',
    problem: 'In preparazione per vendita aziendale, necessità di separazione patrimoniale per massimizzare il valore. Richiesta misurazione certificata e comparabilità del brand per ottimizzare la struttura pre-vendita e proteggere l\'asset strategico.',
    solution: 'Misurazione scientifica e comparabilità del valore del brand secondo metodologia BCI. Separazione patrimoniale con costituzione holding proprietaria del marchio. Struttura di licensing per ottimizzazione fiscale e protezione asset in fase pre-vendita.',
    diagnosis: 'BCI Score: 7.9/10 - Brand con forte valore patrimoniale certificato. Separazione asset intangibile da operatività completata. Valore comparabile quantificato per massimizzare risultato nelle trattative di vendita.',
    results: 'Separazione patrimoniale implementata con successo. Brand protetto e valorizzato separatamente. Ottimizzazione fiscale attraverso royalty deducibili. Valore certificato e comparabile ha incrementato del 28% il prezzo di vendita finale dell\'azienda.',
    brandValue: '€9.2M'
  },
  {
    id: 8,
    title: 'Società di Formazione e Consulenza',
    sector: 'Formazione & Consulenza',
    category: 'Valutazione',
    image: 'https://readdy.ai/api/search-image?query=Professional%20business%20training%20and%20consulting%20company%20office%20with%20modern%20conference%20room%20setup%2C%20sophisticated%20corporate%20education%20environment%2C%20premium%20consulting%20firm%20interior%20with%20elegant%20presentation%20area%2C%20contemporary%20professional%20development%20workspace%2C%20high-end%20business%20consulting%20atmosphere%20with%20refined%20meeting%20spaces%2C%20strategic%20consulting%20office%20design%20with%20professional%20ambiance%2C%20modern%20corporate%20training%20facility%20with%20quality%20furnishings%2C%20elegant%20business%20consulting%20environment%20with%20executive%20setting&width=800&height=600&seq=8&orientation=landscape',
    problem: 'L\'azienda necessitava di valorizzare il brand per creare un asset immateriale tangibile, aumentare la credibilità con banche e clienti corporate, giustificare premium pricing e rafforzare la posizione competitiva nel mercato della consulenza.',
    solution: 'Misurazione scientifica del valore del brand secondo metodologia BCI per creazione asset immateriale certificato. Valorizzazione completa per incremento credibilità bancaria, rafforzamento posizionamento premium e giustificazione pricing superiore alla concorrenza.',
    diagnosis: 'BCI Score: 7.6/10 - Brand professionale con eccellente reputazione e competenze distintive. Valore dell\'asset immateriale quantificato e certificato. Posizionamento premium validato scientificamente per credibilità con stakeholder.',
    results: 'Asset immateriale certificato e inserito a bilancio. Credibilità bancaria aumentata: ottenuto finanziamento €500K con condizioni migliorate. Clienti corporate +35%. Premium pricing giustificato scientificamente: tariffe medie +22% vs concorrenza senza perdita clienti.',
    brandValue: '€5.1M'
  }
];

export default function CaseStudyList() {
  const [selectedCategory, setSelectedCategory] = useState<string>('Tutti');

  const categories = ['Tutti', 'Valutazione', 'Governance', 'Perizia'];

  const filteredCases = selectedCategory === 'Tutti' 
    ? caseStudies 
    : caseStudies.filter(cs => cs.category === selectedCategory);

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-12 sm:mb-16">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 sm:px-8 py-2.5 sm:py-3 rounded-full text-sm sm:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${
                selectedCategory === category
                  ? 'bg-[#E879F9] text-white shadow-lg'
                  : 'bg-gray-100 text-[#0B1F3B] hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Case Studies Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12">
          {filteredCases.map((caseStudy) => (
            <div
              key={caseStudy.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            >
              {/* Image */}
              <div className="w-full h-64 sm:h-72 overflow-hidden">
                <img
                  src={caseStudy.image}
                  alt={caseStudy.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8">
                {/* Header */}
                <div className="flex items-start justify-between mb-4 sm:mb-6">
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0B1F3B] mb-2 sm:mb-3">
                      {caseStudy.title}
                    </h3>
                    <span className="inline-block px-4 py-1.5 bg-[#E879F9] text-white text-xs sm:text-sm font-medium rounded-full">
                      {caseStudy.sector}
                    </span>
                  </div>
                  <div className="ml-4 text-right">
                    <div className="text-xs sm:text-sm text-gray-500 mb-1">Valore Brand</div>
                    <div className="text-2xl sm:text-3xl font-bold text-[#22C55E]">
                      {caseStudy.brandValue}
                    </div>
                  </div>
                </div>

                {/* Problem */}
                <div className="mb-4 sm:mb-5">
                  <div className="flex items-center gap-3 mb-2 sm:mb-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#E879F9] flex items-center justify-center">
                      <i className="ri-error-warning-line text-lg sm:text-xl text-white"></i>
                    </div>
                    <h4 className="text-base sm:text-lg font-semibold text-[#0B1F3B]">Problema</h4>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed pl-0 sm:pl-14">
                    {caseStudy.problem}
                  </p>
                </div>

                {/* Solution */}
                <div className="mb-4 sm:mb-5">
                  <div className="flex items-center gap-3 mb-2 sm:mb-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#E879F9] flex items-center justify-center">
                      <i className="ri-lightbulb-line text-lg sm:text-xl text-white"></i>
                    </div>
                    <h4 className="text-base sm:text-lg font-semibold text-[#0B1F3B]">Soluzione</h4>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed pl-0 sm:pl-14">
                    {caseStudy.solution}
                  </p>
                </div>

                {/* Diagnosis */}
                <div className="mb-4 sm:mb-5 bg-gray-50 rounded-xl p-4 sm:p-5">
                  <div className="flex items-center gap-3 mb-2 sm:mb-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#22C55E] flex items-center justify-center">
                      <i className="ri-stethoscope-line text-lg sm:text-xl text-white"></i>
                    </div>
                    <h4 className="text-base sm:text-lg font-semibold text-[#0B1F3B]">Diagnosi BCI</h4>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed pl-0 sm:pl-14 font-medium">
                    {caseStudy.diagnosis}
                  </p>
                </div>

                {/* Results */}
                <div>
                  <div className="flex items-center gap-3 mb-2 sm:mb-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#22C55E] flex items-center justify-center">
                      <i className="ri-trophy-line text-lg sm:text-xl text-white"></i>
                    </div>
                    <h4 className="text-base sm:text-lg font-semibold text-[#0B1F3B]">Risultati</h4>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed pl-0 sm:pl-14">
                    {caseStudy.results}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
